import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { ApiService } from '../core/api.service';
import { CartStore } from '../core/cart.store';
import { Banner } from '../core/models';
import { LocationStore } from '../core/location.store';

@Component({
  selector: 'gl-site-header',
  imports: [RouterLink, RouterLinkActive, FormsModule],
  template: `
    <header class="site-header">
      @if (promoItems().length) {
        <div class="announcement-ticker" aria-label="Current Grih Upvan offers">
          <div class="promo-marquee">
            <div class="promo-marquee-group">
              @for (item of promoItems(); track item.id ?? $index) {
                <span
                  ><b>{{ item.icon || '•' }}</b> {{ item.title }}</span
                >
              }
            </div>
            <div class="promo-marquee-group" aria-hidden="true">
              @for (item of promoItems(); track item.id ?? $index) {
                <span
                  ><b>{{ item.icon || '•' }}</b> {{ item.title }}</span
                >
              }
            </div>
          </div>
        </div>
      }
      <div class="container header-row">
        <button
          class="icon-button menu-button"
          type="button"
          (click)="navOpen.update((value) => !value)"
          [attr.aria-expanded]="navOpen()"
          aria-controls="primary-nav"
          [attr.aria-label]="navOpen() ? 'Close navigation' : 'Open navigation'"
        >
          ☰
        </button>
        <a
          class="logo header-logo"
          routerLink="/"
          (click)="closeMenus()"
          aria-label="Grih Upvan home"
        >
          <span class="leaf-logo-mark" aria-hidden="true"
            ><svg viewBox="0 0 38 38">
              <path d="M19 34V15"></path>
              <path d="M18 18C8 17 5 10 7 3c8 1 13 7 11 15Z"></path>
              <path d="M20 23c10-1 15-8 13-16-8 1-13 7-13 16Z"></path>
              <path d="M18 29c-7 0-11-4-11-10 7 0 11 4 11 10Z"></path></svg
          ></span>
          <span>Grih Upvan</span>
        </a>
        <nav id="primary-nav" [class.open]="navOpen()" aria-label="Primary navigation">
          <a
            routerLink="/products"
            [queryParams]="{ catalog: 'plants' }"
            routerLinkActive="active"
            (click)="closeNavigation()"
            >Plants</a
          >
          <a
            routerLink="/products"
            [queryParams]="{ catalog: 'pots-planters' }"
            routerLinkActive="active"
            (click)="closeNavigation()"
            >Pots &amp; Planters</a
          >
          <a
            routerLink="/products"
            [queryParams]="{ catalog: 'accessories' }"
            routerLinkActive="active"
            (click)="closeNavigation()"
            >Accessories</a
          >
          <a
            routerLink="/products"
            [queryParams]="{ catalog: 'offers', sort: 'discount' }"
            routerLinkActive="active"
            (click)="closeNavigation()"
            >Offers</a
          >
          <a routerLink="/about" routerLinkActive="active" (click)="closeNavigation()">About</a>
        </nav>
        <div class="header-location">
          <button
            class="header-location-button"
            type="button"
            (click)="location.toggle()"
            [attr.aria-expanded]="location.panelOpen()"
          >
            <span aria-hidden="true">⌖</span><small>Deliver to</small
            ><strong>{{
              location.selection()?.postal_code ||
                location.selection()?.service_location?.name ||
                'Select location'
            }}</strong>
          </button>
          @if (location.panelOpen()) {
            <section class="location-popover" aria-label="Choose delivery location">
              <div class="location-popover-heading">
                <div>
                  <p class="eyebrow">DELIVERY LOCATION</p>
                  <h2>Check availability</h2>
                </div>
                <button
                  type="button"
                  class="icon-button"
                  (click)="location.panelOpen.set(false)"
                  aria-label="Close location selector"
                >
                  ×
                </button>
              </div>
              <p>
                Choose a city for browsing. A valid supported PIN code is required before adding
                products or checking out.
              </p>
              <form (submit)="checkLocation($event)">
                <input
                  name="postalCode"
                  [(ngModel)]="postalCode"
                  inputmode="numeric"
                  autocomplete="postal-code"
                  placeholder="Enter PIN code"
                /><button class="primary-button compact" [disabled]="location.checking()">
                  {{ location.checking() ? 'Checking…' : 'Check' }}
                </button>
              </form>
              @if (location.error()) {
                <p class="location-error" role="alert">{{ location.error() }}</p>
              }
              <div class="supported-location-list">
                @for (option of location.options(); track option.code) {
                  <button type="button" (click)="location.selectLocation(option)">
                    {{ option.name }}<small>{{ option.state }}</small>
                  </button>
                }
              </div>
            </section>
          }
        </div>
        <form class="header-search" role="search" (submit)="search($event)">
          <label class="sr-only" for="global-search">Search products</label>
          <input
            id="global-search"
            name="query"
            [(ngModel)]="query"
            placeholder="Search for plants, planters..."
            autocomplete="off"
          />
          <button class="search-button" aria-label="Submit search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="6.5"></circle>
              <path d="m16 16 4 4"></path>
            </svg>
          </button>
        </form>
        <div class="header-actions">
          <a class="header-action-link" routerLink="/account/wishlist" aria-label="Wishlist"
            ><span class="header-action-icon">♡</span><small>Wishlist</small></a
          >
          <div class="header-profile-menu">
            <button
              class="header-action-link header-profile-trigger"
              type="button"
              (click)="accountMenuOpen.update((value) => !value)"
              [attr.aria-expanded]="accountMenuOpen()"
              aria-controls="header-account-menu"
              aria-label="Profile menu"
            >
              <span class="header-action-icon profile-icon" aria-hidden="true"
                ><svg viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="3.5"></circle>
                  <path d="M5.5 20c.5-4.2 2.7-6.2 6.5-6.2s6 2 6.5 6.2"></path></svg></span
              ><small>Profile</small>
            </button>
            @if (accountMenuOpen()) {
              <div class="header-profile-popover" id="header-account-menu">
                @if (api.isAuthenticated()) {
                  <a routerLink="/account" (click)="closeMenus()">My Account</a>
                  <a routerLink="/account/orders" (click)="closeMenus()">My Orders</a>
                  <a routerLink="/account/addresses" (click)="closeMenus()">My Addresses</a>
                  <button
                    type="button"
                    class="header-logout-button"
                    (click)="logout()"
                    [disabled]="loggingOut()"
                  >
                    {{ loggingOut() ? 'Signing out…' : 'Logout' }}
                  </button>
                } @else {
                  <a routerLink="/login" (click)="closeMenus()">Login</a>
                  <a routerLink="/register" (click)="closeMenus()">Create account</a>
                }
              </div>
            }
          </div>
          <a class="header-action-link cart-link" routerLink="/cart" aria-label="Bag"
            ><span class="header-action-icon bag-icon" aria-hidden="true"
              ><svg viewBox="0 0 24 24">
                <path d="M5 8h14l-1 12H6L5 8Z"></path>
                <path d="M9 9V6a3 3 0 0 1 6 0v3"></path></svg></span
            ><small>Bag</small>
            @if (cart.count()) {
              <b>{{ cart.count() }}</b>
            }
          </a>
        </div>
      </div>
    </header>
  `,
})
export class SiteHeaderComponent {
  readonly cart = inject(CartStore);
  readonly location = inject(LocationStore);
  readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly navOpen = signal(false);
  readonly accountMenuOpen = signal(false);
  readonly loggingOut = signal(false);
  readonly promoItems = signal<Banner[]>([]);
  query = '';
  postalCode = '';

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeMenus());
    this.api.get<Banner[]>('/banners', { placement: 'promo_bar' }).subscribe({
      next: (response) => {
        if (response.data.length) this.promoItems.set(response.data);
      },
    });
  }

  closeNavigation() {
    this.navOpen.set(false);
  }
  closeMenus() {
    this.navOpen.set(false);
    this.accountMenuOpen.set(false);
  }
  logout() {
    if (this.loggingOut()) return;
    this.loggingOut.set(true);
    this.api.logout().subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout(),
    });
  }
  private finishLogout() {
    this.loggingOut.set(false);
    this.closeMenus();
    this.router.navigate(['/login']);
  }
  search(event: Event) {
    event.preventDefault();
    const q = this.query.trim();
    if (q) {
      this.closeMenus();
      this.router.navigate(['/search'], { queryParams: { q } });
    }
  }
  checkLocation(event: Event) {
    event.preventDefault();
    this.location.checkPostal(this.postalCode);
  }
}
